import { useEffect, useState } from 'react';

/**
 * Returns the last underscore-delimited segment from a nested selection id.
 */
const getLastSegment = (nestedId: string): string => {
  const parts = String(nestedId).split('_');
  return parts[parts.length - 1] ?? '';
};

/**
 * Returns the display-friendly leaf id from a node id.
 * Handles ids with list prefixes (".") and nested segments ("_" or "depth:id").
 */
export const getDisplayIdFromNodeId = (nodeId: string): string => {
  const lastSegment = getLastSegment(String(nodeId));
  const dotParts = lastSegment.split('.');
  const dotLast = dotParts[dotParts.length - 1] ?? lastSegment;
  if (dotLast.includes(':')) {
    const segParts = dotLast.split(':');
    return segParts[segParts.length - 1] ?? dotLast;
  }
  return dotLast;
};

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  if (typeof document === 'undefined' || !document.body) {
    return false;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', 'true');
  textArea.style.position = 'absolute';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();

  const isCopied = document.execCommand('copy');
  document.body.removeChild(textArea);

  return isCopied;
};

const listeners = new Set<(value: boolean) => void>();
let isModifierPressed = false;
let isListening = false;

const canUseDom = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const notifyListeners = () => {
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  listeners.forEach((listener) => listener(isModifierPressed));
};

const setModifierPressed = (nextValue: boolean) => {
  if (nextValue === isModifierPressed) return;
  isModifierPressed = nextValue;
  notifyListeners();
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.metaKey || event.ctrlKey) {
    setModifierPressed(true);
  }
};

const handleKeyUp = (event: KeyboardEvent) => {
  if (event.key === 'Meta' || event.key === 'Control') {
    setModifierPressed(event.metaKey || event.ctrlKey);
  }
};

const handleBlur = () => {
  setModifierPressed(false);
};

const startListening = () => {
  if (isListening || !canUseDom()) return;
  isListening = true;
  document.addEventListener('keydown', handleKeyDown);
  document.addEventListener('keyup', handleKeyUp);
  window.addEventListener('blur', handleBlur);
};

const stopListening = () => {
  if (!isListening || !canUseDom()) return;
  isListening = false;
  document.removeEventListener('keydown', handleKeyDown);
  document.removeEventListener('keyup', handleKeyUp);
  window.removeEventListener('blur', handleBlur);
  setModifierPressed(false);
};

const subscribe = (listener: (value: boolean) => void) => {
  listeners.add(listener);
  startListening();
  listener(isModifierPressed);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopListening();
    }
  };
};

/**
 * Hook to track when a modifier key (Cmd on Mac, Ctrl on Windows/Linux) is pressed.
 * Returns true when the modifier key is currently held down.
 */
export const useModifierKeyPressed = () => {
  const [isPressed, setIsPressed] = useState(isModifierPressed);

  useEffect(() => {
    return subscribe(setIsPressed);
  }, []);

  return isPressed;
};
